import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Coding, CodeableConcept, Quantity, Range } from 'fhir/r4';
import { Observable, Subject, finalize, map, of, startWith, takeUntil } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { ObservationReferenceRange, ObservationReferenceRangeService, ObservationReferenceRangeTemplate } from './observation-reference-range.service';
import { ReferenceRangeImportExportService } from './reference-range-import-export.service';

interface ReferenceRangeForm {
  id: FormControl<string | null>;
  code: FormControl<Coding | string | null>;
  display: FormControl<string>;
  type: FormControl<string>;
  lowValue: FormControl<number | null>;
  lowUnit: FormControl<string>;
  highValue: FormControl<number | null>;
  highUnit: FormControl<string>;
  appliesTo: FormControl<CodeableConcept[]>;
  ageLowValue: FormControl<number | null>;
  ageLowUnit: FormControl<string>;
  ageHighValue: FormControl<number | null>;
  ageHighUnit: FormControl<string>;
  text: FormControl<string>;
}

interface ConceptOption {
  label: string;
  value: string;
  concept: CodeableConcept;
}

@Component({
  selector: 'app-observation-reference-ranges',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTableModule,
    MatTooltipModule
  ],
  templateUrl: './observation-reference-ranges.component.html',
  styleUrls: ['./observation-reference-ranges.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationReferenceRangesComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly unitSystem = 'http://unitsofmeasure.org';
  private readonly maxAutocompleteResults = 50;

  readonly form: FormGroup<ReferenceRangeForm>;

  observationCodes: Coding[] = [];
  filteredCodes$: Observable<Coding[]> = of([]);
  templates: ObservationReferenceRangeTemplate[] = [];

  loadingCodes = false;
  loadingTemplates = false;
  savingTemplate = false;

  readonly displayedColumns = ['code', 'range', 'appliesTo', 'age', 'updated', 'actions'];

  readonly rangeTypeOptions: ConceptOption[] = [
    this.buildRangeTypeOption('normal', 'Normal range'),
    this.buildRangeTypeOption('critical', 'Critical range'),
    this.buildRangeTypeOption('recommended', 'Recommended range')
  ];

  readonly appliesToOptions: ConceptOption[] = [
    this.buildAppliesToOption('female', 'Female'),
    this.buildAppliesToOption('male', 'Male'),
    this.buildAppliesToOption('other', 'Other / Non-binary'),
    this.buildAppliesToOption('pregnant', 'Pregnant Patients')
  ];

  readonly ageUnitOptions = [
    { value: 'a', label: 'Years' },
    { value: 'mo', label: 'Months' },
    { value: 'wk', label: 'Weeks' },
    { value: 'd', label: 'Days' }
  ];

  readonly compareConcepts = (a?: CodeableConcept | null, b?: CodeableConcept | null) => {
    if (!a || !b) {
      return a === b;
    }
    const aCode = a.coding?.[0]?.code ?? a.text;
    const bCode = b.coding?.[0]?.code ?? b.text;
    return aCode === bCode;
  };

  constructor(
    private readonly fb: FormBuilder,
    private readonly selectDataService: FormFieldsSelectDataService,
    private readonly referenceRangesService: ObservationReferenceRangeService,
    private readonly importExportService: ReferenceRangeImportExportService,
    private readonly snackBar: MatSnackBar
  ) {
    this.form = this.buildForm();
  }

  ngOnInit(): void {
    this.loadObservationCodes();
    this.loadTemplates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get codeControl(): FormControl<Coding | string | null> {
    return this.form.controls.code;
  }

  displayCodeOption = (value: Coding | string | null): string => {
    if (!value) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.display || value.code || '';
  };

  submitRange(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const template = this.mapFormToTemplate();
    if (!template) {
      this.showMessage('Select a valid observation code before saving.');
      return;
    }

    this.savingTemplate = true;
    this.referenceRangesService.saveTemplate(template).pipe(
      finalize(() => this.savingTemplate = false),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.showMessage('Reference range saved.');
        this.loadTemplates();
        this.resetForm();
      },
      error: () => this.showMessage('Failed to save reference range. Please try again.')
    });
  }

  editTemplate(template: ObservationReferenceRangeTemplate): void {
    const range = template.range ?? {};
    this.form.patchValue({
      id: template.id ?? null,
      code: template.code ?? null,
      display: template.display ?? '',
      type: this.resolveRangeType(range.type),
      lowValue: range.low?.value ?? null,
      lowUnit: range.low?.unit || range.low?.code || '',
      highValue: range.high?.value ?? null,
      highUnit: range.high?.unit || range.high?.code || '',
      appliesTo: range.appliesTo ?? [],
      ageLowValue: range.age?.low?.value ?? null,
      ageLowUnit: range.age?.low?.code || range.age?.low?.unit || 'a',
      ageHighValue: range.age?.high?.value ?? null,
      ageHighUnit: range.age?.high?.code || range.age?.high?.unit || 'a',
      text: range.text ?? ''
    });
  }

  resetForm(): void {
    this.form.reset({
      id: null,
      code: null,
      display: '',
      type: 'normal',
      lowValue: null,
      lowUnit: '',
      highValue: null,
      highUnit: '',
      appliesTo: [],
      ageLowValue: null,
      ageLowUnit: 'a',
      ageHighValue: null,
      ageHighUnit: 'a',
      text: ''
    });
  }

  deleteTemplate(template: ObservationReferenceRangeTemplate): void {
    if (!template.id) {
      return;
    }
    const confirmed = window.confirm('Delete this reference range template?');
    if (!confirmed) {
      return;
    }
    this.referenceRangesService.deleteTemplate(template.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.showMessage('Reference range deleted.');
        this.loadTemplates();
      },
      error: () => this.showMessage('Failed to delete reference range.')
    });
  }

  rangeSummary(range?: ObservationReferenceRange): string {
    if (!range) {
      return '—';
    }
    const parts: string[] = [];
    if (range.low?.value != null) {
      parts.push(`Low: ${range.low.value} ${range.low.unit || range.low.code || ''}`.trim());
    }
    if (range.high?.value != null) {
      parts.push(`High: ${range.high.value} ${range.high.unit || range.high.code || ''}`.trim());
    }
    if (!parts.length && range.text) {
      parts.push(range.text);
    }
    return parts.length ? parts.join(' · ') : 'Configured';
  }

  appliesToSummary(range?: ObservationReferenceRange): string {
    const applies = range?.appliesTo ?? [];
    if (!applies.length) {
      return 'All patients';
    }
    return applies
      .map(concept => concept.text || concept.coding?.[0]?.display || concept.coding?.[0]?.code || 'Unknown')
      .join(', ');
  }

  ageSummary(range?: ObservationReferenceRange): string {
    const age = range?.age;
    if (!age || (!age.low && !age.high)) {
      return 'All ages';
    }
    const segments: string[] = [];
    if (age.low?.value != null) {
      segments.push(`≥ ${age.low.value} ${age.low.unit || age.low.code}`.trim());
    }
    if (age.high?.value != null) {
      segments.push(`≤ ${age.high.value} ${age.high.unit || age.high.code}`.trim());
    }
    return segments.join(' and ');
  }

  downloadTemplateCSV(): void {
    this.importExportService.downloadCsvTemplate();
    this.showMessage('Template CSV downloaded. Fill it and upload to bulk-import ranges.');
  }

  downloadTemplatesAsCSV(): void {
    if (!this.templates.length) {
      this.showMessage('No templates to export.');
      return;
    }
    this.importExportService.downloadTemplatesAsCSV(this.templates);
    this.showMessage(`Exported ${this.templates.length} templates to CSV.`);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const { templates, errors } = this.importExportService.parseCSV(content);

        if (errors.length > 0) {
          const errorMsg = errors.slice(0, 3).join('\n');
          this.showMessage(`Import errors (showing first 3):\n${errorMsg}`, 'error');
          if (errors.length > 3) {
            console.warn(`Total import errors: ${errors.length}`);
          }
        }

        if (templates.length === 0) {
          this.showMessage('No valid templates found in CSV.');
          return;
        }

        // Auto-submit imported templates
        let successCount = 0;
        let failureCount = 0;

        const submitNext = (index: number) => {
          if (index >= templates.length) {
            this.loadTemplates();
            this.showMessage(
              `Imported ${successCount} template(s). ${failureCount > 0 ? ` ${failureCount} failed.` : ''}`,
              failureCount > 0 ? 'error' : 'success'
            );
            input.value = ''; // Clear file input
            return;
          }

          const template = templates[index];
          this.referenceRangesService.saveTemplate(template).subscribe({
            next: () => {
              successCount++;
              submitNext(index + 1);
            },
            error: () => {
              failureCount++;
              submitNext(index + 1);
            }
          });
        };

        submitNext(0);
      } catch (err) {
        this.showMessage(`Failed to parse CSV: ${(err as any)?.message || String(err)}`, 'error');
        input.value = '';
      }
    };
    reader.readAsText(file);
  }

  private buildForm(): FormGroup<ReferenceRangeForm> {
    return this.fb.group<ReferenceRangeForm>({
      id: this.fb.control<string | null>(null),
      code: this.fb.control<Coding | string | null>(null, { validators: Validators.required }),
      display: this.fb.nonNullable.control(''),
      type: this.fb.nonNullable.control('normal', { validators: Validators.required }),
      lowValue: this.fb.control<number | null>(null),
      lowUnit: this.fb.nonNullable.control(''),
      highValue: this.fb.control<number | null>(null),
      highUnit: this.fb.nonNullable.control(''),
      appliesTo: this.fb.nonNullable.control<CodeableConcept[]>([]),
      ageLowValue: this.fb.control<number | null>(null),
      ageLowUnit: this.fb.nonNullable.control('a'),
      ageHighValue: this.fb.control<number | null>(null),
      ageHighUnit: this.fb.nonNullable.control('a'),
      text: this.fb.nonNullable.control('')
    });
  }

  private loadObservationCodes(): void {
    this.loadingCodes = true;
    this.selectDataService
      .getFormFieldSelectData('observation', 'localCode')
      .pipe(
        finalize(() => this.loadingCodes = false),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (codes: Coding[]) => {
          this.observationCodes = Array.isArray(codes) ? codes : [];
          this.filteredCodes$ = this.codeControl.valueChanges.pipe(
            startWith(this.codeControl.value ?? ''),
            map(value => typeof value === 'string' ? value : value?.display || value?.code || ''),
            map(search => this.filterCodes(search))
          );
        },
        error: () => {
          this.observationCodes = [];
          this.filteredCodes$ = of([]);
          this.showMessage('Unable to load local observation codes.');
        }
      });
  }

  private loadTemplates(): void {
    this.loadingTemplates = true;
    this.referenceRangesService
      .listTemplates()
      .pipe(
        finalize(() => this.loadingTemplates = false),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: templates => this.templates = templates ?? [],
        error: () => {
          this.templates = [];
          this.showMessage('Unable to load saved reference ranges.');
        }
      });
  }

  private filterCodes(search: string): Coding[] {
    if (!this.observationCodes?.length) {
      return [];
    }
    const normalized = (search ?? '').toLowerCase();
    if (!normalized) {
      return this.observationCodes.slice(0, this.maxAutocompleteResults);
    }
    return this.observationCodes
      .filter(coding => (coding.display || '').toLowerCase().includes(normalized) || (coding.code || '').toLowerCase().includes(normalized))
      .slice(0, this.maxAutocompleteResults);
  }

  private mapFormToTemplate(): ObservationReferenceRangeTemplate | null {
    const value = this.form.getRawValue();
    const coding = this.normalizeCoding(value.code);
    if (!coding) {
      return null;
    }

    const range: ObservationReferenceRange = {
      type: this.toRangeTypeConcept(value.type),
      low: this.toQuantity(value.lowValue, value.lowUnit),
      high: this.toQuantity(value.highValue, value.highUnit),
      appliesTo: value.appliesTo?.length ? value.appliesTo : undefined,
      age: this.toAgeRange(value),
      text: value.text?.trim() || undefined
    };

    return {
      id: value.id || undefined,
      code: coding,
      display: value.display?.trim() || coding.display || coding.code,
      range
    };
  }

  private toQuantity(value: number | null, unit: string): Quantity | undefined {
    if (value == null || Number.isNaN(value)) {
      return undefined;
    }
    const trimmedUnit = unit?.trim();
    return {
      value,
      system: this.unitSystem,
      code: trimmedUnit || undefined,
      unit: trimmedUnit || undefined
    };
  }

  private toAgeRange(value: ReturnType<typeof this.form.getRawValue>): Range | undefined {
    const low = this.toQuantity(value.ageLowValue, value.ageLowUnit);
    const high = this.toQuantity(value.ageHighValue, value.ageHighUnit);
    if (!low && !high) {
      return undefined;
    }
    return { low, high };
  }

  private normalizeCoding(value: Coding | string | null): Coding | null {
    if (!value) {
      return null;
    }
    if (typeof value !== 'string') {
      return value;
    }
    const match = this.observationCodes.find(coding => {
      const display = coding.display?.toLowerCase() ?? '';
      const code = coding.code?.toLowerCase() ?? '';
      const search = value.toLowerCase();
      return display === search || code === search;
    });
    return match ?? null;
  }

  private toRangeTypeConcept(value: string | null): CodeableConcept | undefined {
    if (!value) {
      return undefined;
    }
    return this.rangeTypeOptions.find(option => option.value === value)?.concept;
  }

  private resolveRangeType(concept?: CodeableConcept): string {
    const code = concept?.coding?.[0]?.code;
    return this.rangeTypeOptions.find(option => option.concept.coding?.[0]?.code === code)?.value ?? 'normal';
  }

  private buildRangeTypeOption(code: string, label: string): ConceptOption {
    return {
      value: code,
      label,
      concept: {
        coding: [{
          system: 'http://terminology.hl7.org/CodeSystem/referencerange-meaning',
          code,
          display: label
        }],
        text: label
      }
    };
  }

  private buildAppliesToOption(code: string, label: string): ConceptOption {
    return {
      value: code,
      label,
      concept: {
        coding: [{
          system: 'http://hl7.org/fhir/administrative-gender',
          code,
          display: label
        }],
        text: label
      }
    };
  }

  private showMessage(message: string, severity?: 'error' | 'success'): void {
    const config: any = { duration: 3500 };
    if (severity === 'error') {
      config.panelClass = 'snackbar-error';
    } else if (severity === 'success') {
      config.panelClass = 'snackbar-success';
    }
    this.snackBar.open(message, 'Dismiss', config);
  }
}
